import './DotChart.css';

import { useWindowResize } from '@toggle/helpers';
import { getInstanceByDom, init } from 'echarts';
import React, { useEffect, useMemo, useRef } from 'react';

import { formatDecimalPlaces } from '~/components/table/utils';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
} from '../apache-config';
import {
  checkIsBubbleSizeSame,
  DotChartSeries,
  DotChartsNames,
  formatDotSeries,
  getFEDotChartProps,
} from './utils';

const tooltipRow = (label?: string, value?: string | number) => {
  if (!label && !value) {
    return '';
  }

  return `<p class="tooltip-row">
      <span>${label}</span>
      <span>${formatDecimalPlaces(Number(value))}</span>
    </p>`;
};

export enum DotChartEnum {
  ApacheAxisBubble = 'apache-axis-bubble',
  ApacheBubble = 'apache-bubble',
  ApacheBubbleTime = 'apache-bubble-time',
}

export interface DotChartProps extends ApacheChartBase {
  chartType: DotChartsNames;
  series: DotChartSeries;
  axisLabels?: string[];
}

export const DotChart = ({
  theme = 'dark_default',
  series,
  chartType,
  axisLabels,
  onInit,
  isModal,
}: DotChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  const isBubbleSizeSame = useMemo(() => {
    return checkIsBubbleSizeSame({
      series,
      chartType,
    });
  }, [series, chartType]);

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = ref.current?.clientWidth || DEFAULT_CHART_WIDTH;
    const formattedSeries = formatDotSeries({
      series,
      options: { chartType },
      chartWidth,
      isSizeIdentical: isBubbleSizeSame,
    });
    const baseOption = createApacheConfig({
      theme,
      isModal,
      options: {
        xAxis: {
          scale: true,
          show: chartType !== 'apache-axis-bubble',
          name: axisLabels?.[0],
        },
        yAxis: {
          scale: true,
          show: chartType !== 'apache-axis-bubble',
          name: axisLabels?.[1],
        },
        series: chartType === 'apache-bubble-time' ? [] : formattedSeries,
        tooltip: {
          formatter: params => {
            const parsedParams =
              params as echarts.DefaultLabelFormatterCallbackParams;
            const dataArray = parsedParams.data as (string | number)[];

            if (!dataArray.length && !axisLabels?.length) {
              return '';
            }

            return `<div>
            <div>
              ${parsedParams.marker}
              ${parsedParams.seriesName}
            </div>
            ${dataArray
              .map((item, index) => {
                let labelIndex = index;

                if (
                  chartType === 'apache-axis-bubble' &&
                  index === dataArray.length - 1
                ) {
                  labelIndex = index + 1;
                }

                const label = axisLabels?.[labelIndex];

                if ((isBubbleSizeSame && index === 2) || !label) {
                  return '';
                }

                return `${tooltipRow(label, item)}`;
              })
              .join('')}
          </div>`;
          },
        },
        ...getFEDotChartProps({
          chartType,
          series,
          axisLabels,
          theme,
          isSizeIdentical: isBubbleSizeSame,
        }),
      },
    });

    const options =
      chartType === 'apache-bubble-time' ? (formattedSeries as any) : [];

    chartDom.setOption({ baseOption, options });

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [
    isModal,
    chartType,
    theme,
    series,
    axisLabels,
    isBubbleSizeSame,
    width,
    height,
  ]);

  return <div ref={ref} />;
};
